import type { StoreApi } from "zustand/vanilla";
import type { VoiceAgentStore } from "@moss-js/founding-agent/core";
import { MIC_ERROR_COPY } from "@moss-js/founding-agent/core";

export interface MicErrorOverlayHandle {
  destroy(): void;
}

export function createMicErrorOverlay(
  root: HTMLElement,
  store: StoreApi<VoiceAgentStore>,
): MicErrorOverlayHandle {
  let overlayEl: HTMLDivElement | null = null;

  function render(state: ReturnType<typeof store.getState>): void {
    const { micError } = state;

    if (micError !== null) {
      if (!overlayEl) {
        overlayEl = document.createElement("div");
        overlayEl.setAttribute("data-moss-mic-error", "");
        overlayEl.setAttribute("role", "alert");
        overlayEl.setAttribute("aria-live", "assertive");
        root.appendChild(overlayEl);
      }

      overlayEl.textContent = "";

      const copy = MIC_ERROR_COPY[micError];

      const titleEl = document.createElement("p");
      titleEl.setAttribute("data-moss-mic-title", "");
      titleEl.textContent = copy.title;
      overlayEl.appendChild(titleEl);

      const bodyEl = document.createElement("p");
      bodyEl.setAttribute("data-moss-mic-body", "");
      bodyEl.textContent = copy.body;
      overlayEl.appendChild(bodyEl);

      const retryBtn = document.createElement("button");
      retryBtn.type = "button";
      retryBtn.setAttribute("data-moss-mic-retry", "");
      retryBtn.textContent = "Try again";
      retryBtn.addEventListener("click", () => {
        // Do NOT clear micError optimistically here. The transport's retryMic
        // clears it only after setMicrophoneEnabled succeeds, and keeps/re-sets
        // it on failure, so the overlay stays until the mic actually works.
        store.getState().requestMicRetry();
      });
      overlayEl.appendChild(retryBtn);
    } else {
      if (overlayEl) {
        overlayEl.remove();
        overlayEl = null;
      }
    }
  }

  // Gate on micError only. The subscription fires on every store write
  // (audio levels tick ~25Hz during a call); rebuilding the overlay each time
  // is wasteful and churns the DOM. Rebuild only when the error itself changes.
  let prevMicError = store.getState().micError;
  render(store.getState());

  const unsub = store.subscribe((state) => {
    if (state.micError !== prevMicError) {
      prevMicError = state.micError;
      render(state);
    }
  });

  return {
    destroy() {
      unsub();
      overlayEl?.remove();
      overlayEl = null;
    },
  };
}
